import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Sparkles, Save, ArrowLeft, FileText, ListChecks, Layers,
  PenLine, Presentation, Languages, HelpCircle, ClipboardList, Trash2,
  Copy, CheckCircle2, BookOpen, Globe, Share2, Lock, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { subjectThemeClass } from "@/lib/subject-theme";
import { logQuietError } from "@/lib/quiet-error";
import Markdown from "@/components/Markdown";
import { Skeleton } from "@/components/Skeleton";
import ExportMenu from "@/components/ExportMenu";

const ARTIFACT_DEFS = [
  { key: "lesson_plan",  label: "Lesson plan",  icon: PenLine },
  { key: "quiz",         label: "Quiz",         icon: ListChecks },
  { key: "worksheet",    label: "Worksheet",    icon: FileText },
  { key: "flashcards",   label: "Flashcards",   icon: Layers },
  { key: "homework",     label: "Homework",     icon: ClipboardList },
  { key: "ppt_outline",  label: "Presentation", icon: Presentation },
  { key: "simplified",   label: "Simplified",   icon: HelpCircle },
  { key: "translated",   label: "Translation",  icon: Languages },
];

export default function LessonWorkspace() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genKind, setGenKind] = useState(null);
  const [tab, setTab] = useState("notes");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lessons/${lessonId}`);
      setLesson(data);
      // pick first artifact tab if exists
      const firstArt = Object.keys(data.artifacts || {})[0];
      setTab(firstArt || "notes");
      // fetch share analytics inline (relative path)
      try {
        const r = await api.get(`/lessons/${lessonId}/share-analytics`);
        setShareInfo(r.data);
      } catch (e) { logQuietError("lesson.share-analytics", e); }
    } catch (e) {
      logQuietError("lesson.load", e);
      toast.error("Lesson not found");
      navigate("/lessons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lessonId]);

  const loadShareInfo = async () => {
    try {
      const { data } = await api.get(`/lessons/${lessonId}/share-analytics`);
      setShareInfo(data);
    } catch (e) { logQuietError("lesson.share-analytics.reload", e); }
  };

  const publish = async (includeNotes = false, includeSource = false) => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/publish`, {
        include_notes: includeNotes,
        include_source: includeSource,
      });
      const publicUrl = `${window.location.origin}${data.public_url_path}`;
      setShareInfo({
        ...(shareInfo || {}),
        is_published: true,
        share_token: data.share_token,
        public_url_path: data.public_url_path,
        published_at: data.published_at,
      });
      try { await navigator.clipboard.writeText(publicUrl); } catch (e) { logQuietError("lesson.publish.clipboard", e); }
      toast.success("Published! Public link copied to your clipboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not publish");
    } finally { setPublishing(false); }
  };

  const unpublish = async () => {
    setPublishing(true);
    try {
      await api.post(`/lessons/${lessonId}/unpublish`);
      setShareInfo((s) => ({ ...(s || {}), is_published: false }));
      toast.success("Lesson is private again");
    } catch { toast.error("Could not unpublish"); }
    finally { setPublishing(false); }
  };

  const copyPublicLink = async () => {
    if (!shareInfo?.share_token) return;
    const url = `${window.location.origin}/l/${shareInfo.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const save = async () => {
    if (!lesson) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/lessons/${lessonId}`, {
        title: lesson.title,
        subject: lesson.subject,
        grade: lesson.grade,
        curriculum: lesson.curriculum,
        language: lesson.language,
        notes: lesson.notes,
      });
      setLesson(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const generate = async (kind) => {
    setGenKind(kind);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/artifacts`, { kind });
      setLesson(data);
      setTab(kind);
      toast.success(`${ARTIFACT_DEFS.find((d) => d.key === kind)?.label || kind} generated`);
    } catch { toast.error("Generation failed"); }
    finally { setGenKind(null); }
  };

  const deleteLesson = async () => {
    if (!window.confirm("Delete this lesson? This can't be undone.")) return;
    try {
      await api.delete(`/lessons/${lessonId}`);
      toast.success("Lesson deleted");
      navigate("/lessons");
    } catch { toast.error("Delete failed"); }
  };

  const copyActive = async () => {
    if (!lesson) return;
    const content = tab === "notes" ? (lesson.notes || "") : (lesson.artifacts?.[tab]?.content || "");
    try { await navigator.clipboard.writeText(content); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  // Stable filter result — must be declared above any early return to obey Rules of Hooks.
  const visibleArtifactDefs = useMemo(
    () => ARTIFACT_DEFS.filter((a) => lesson?.artifacts?.[a.key]),
    [lesson?.artifacts]
  );

  if (loading) {
    return (
      <Layout title="Lesson workspace">
        <div className="space-y-4 max-w-4xl">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-72 w-full" />
        </div>
      </Layout>
    );
  }
  if (!lesson) return null;

  const artifactKeys = Object.keys(lesson.artifacts || {});

  return (
    <Layout title={lesson.title || "Lesson workspace"}>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" className="rounded-full" onClick={() => navigate("/lessons")} data-testid="lesson-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> All lessons
        </Button>
        <div className="flex gap-2 flex-wrap">
          {shareInfo?.is_published ? (
            <Button variant="outline" size="sm" className="rounded-full text-primary border-primary/40" onClick={() => setShareOpen(true)} data-testid="lesson-shared-pill">
              <Globe className="h-3.5 w-3.5 mr-1.5" /> Shared · {shareInfo.view_count || 0} views
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShareOpen(true)} data-testid="lesson-publish-cta">
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Publish
            </Button>
          )}
          <Button variant="outline" size="sm" className="rounded-full text-destructive hover:text-destructive" onClick={deleteLesson} data-testid="lesson-delete">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
          </Button>
          <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid="lesson-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left: lesson metadata + artifact rail */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="tb-card p-5 space-y-3" data-testid="lesson-meta">
            <div>
              <Label>Title</Label>
              <Input
                value={lesson.title || ""}
                onChange={(e) => setLesson({ ...lesson, title: e.target.value })}
                className="mt-1.5 h-11 rounded-xl"
                data-testid="lesson-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Subject</Label>
                <Input value={lesson.subject || ""} onChange={(e) => setLesson({ ...lesson, subject: e.target.value })} className="mt-1.5 h-10 rounded-xl" />
              </div>
              <div>
                <Label>Grade</Label>
                <Input value={lesson.grade || ""} onChange={(e) => setLesson({ ...lesson, grade: e.target.value })} className="mt-1.5 h-10 rounded-xl" />
              </div>
              <div>
                <Label>Curriculum</Label>
                <Input value={lesson.curriculum || ""} onChange={(e) => setLesson({ ...lesson, curriculum: e.target.value })} className="mt-1.5 h-10 rounded-xl" />
              </div>
              <div>
                <Label>Language</Label>
                <Input value={lesson.language || ""} onChange={(e) => setLesson({ ...lesson, language: e.target.value })} className="mt-1.5 h-10 rounded-xl" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground pt-1">{lesson.attached_documents?.length || 0} attached document(s)</p>
          </div>

          {/* Artifact generator rail */}
          <div className="tb-card p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> Generate artifact
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {ARTIFACT_DEFS.map((a) => {
                const Icon = a.icon;
                const ready = !!lesson.artifacts?.[a.key];
                const busy = genKind === a.key;
                return (
                  <button
                    key={a.key}
                    onClick={() => generate(a.key)}
                    disabled={!!genKind}
                    className={`flex items-center gap-2 rounded-xl p-2.5 border text-left transition ${
                      ready
                        ? "border-primary/40 bg-primary/5"
                        : "border-border hover:border-primary/40 bg-card/60"
                    } ${busy ? "opacity-60" : ""}`}
                    data-testid={`generate-${a.key}`}
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" /> : <Icon className="h-4 w-4 text-foreground shrink-0" />}
                    <span className="text-xs font-medium flex-1">{a.label}</span>
                    {ready && !busy && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Right: notes + artifact canvas */}
        <section className="lg:col-span-8 space-y-4">
          {/* Tabs */}
          <div className="tb-card p-2">
            <div className="flex gap-1 overflow-x-auto tb-no-scrollbar">
              <TabButton k="notes" tab={tab} setTab={setTab} icon={BookOpen} label="Notes" />
              {visibleArtifactDefs.map((a) => (
                <TabButton key={a.key} k={a.key} tab={tab} setTab={setTab} icon={a.icon} label={a.label} />
              ))}
              {artifactKeys.length === 0 && (
                <p className="text-xs text-muted-foreground p-2 italic">Generate an artifact to see it here</p>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div className="tb-card p-6 min-h-[420px]" data-testid="lesson-canvas">
            <div className="flex items-center justify-end gap-2 mb-3">
              {tab !== "notes" && lesson.artifacts?.[tab] && (
                <>
                  <Button onClick={copyActive} size="sm" variant="outline" className="rounded-full" data-testid="lesson-canvas-copy">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                  </Button>
                  <ExportMenu title={lesson.artifacts[tab].title} content={lesson.artifacts[tab].content} includePptx={tab === "ppt_outline" || tab === "lesson_plan" || tab === "worksheet"} />
                </>
              )}
            </div>

            {tab === "notes" ? (
              <>
                <Label>Teacher notes</Label>
                <Textarea
                  rows={16}
                  value={lesson.notes || ""}
                  onChange={(e) => setLesson({ ...lesson, notes: e.target.value })}
                  placeholder="Lesson observations, prep checklist, things to remember…"
                  className="mt-1.5 rounded-xl"
                  data-testid="lesson-notes"
                />
              </>
            ) : lesson.artifacts?.[tab] ? (
              <Markdown className={subjectThemeClass(lesson.subject)}>{lesson.artifacts[tab].content}</Markdown>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-12">Tap the artifact in the side rail to generate it.</p>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function TabButton({ k, tab, setTab, icon: Icon, label }) {
  const active = tab === k;
  return (
    <button
      onClick={() => setTab(k)}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition ${
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
      data-testid={`canvas-tab-${k}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

/* ------ Share dialog ------ */
function _UnusedShareDialogPlaceholder() { return null; }
